import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Options } from '@angular-slider/ngx-slider';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  dayAhead,
  DayAheadMarketConfig,
  HourlyConfigColumn,
  PermissionEnum,
  PricesToOffer,
  ProgramConfig,
  Site,
} from '@model';
import {
  BidService,
  BidStrategyService,
  ForecastService,
  GlobalAlertService,
  MarketsConfigService,
  PermissionService,
  ProgramService,
  SiteService,
} from '@service';
import { HourlyConfigModalComponent } from '../../shared/component/hourly-config-modal/hourly-config-modal.component';
import { ExportModalComponent } from '../export-modal/export-modal.component';

@Component({
  selector: 'bmd-da-market',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  styleUrls: ['./day-ahead-market.component.css'],
  templateUrl: './day-ahead-market.component.html',
  viewProviders: [MatExpansionPanel],
})
export class DayAheadMarketComponent implements OnInit, OnDestroy, OnChanges {
  @Input() site: Site;
  @Input() cron: string;
  @Input() programConfig: ProgramConfig;
  showHourlyRiskProfileButton: boolean | string = false;
  showHourlyASPricesButton: boolean | string = false;
  value = 0;
  riskProfileDayAhead = 0;
  max = 100;
  min = 0;
  step = 50;

  options: Options = {
    showTicksValues: true,
    stepsArray: [
      { legend: 'Low', value: 0 },
      { legend: 'Medium', value: 50 },
      { legend: 'High', value: 100 },
    ],
  };
  private subject$: Subject<void>;

  dayAheadForm: FormGroup = new FormGroup({
    dayAheadForecaster: new FormControl(''),
    ecrsMarketDayAhead: new FormControl(false),
    energyMarketDayAhead: new FormControl(false),
    ffrMarketDayAhead: new FormControl(false),
    loadMarketDayAhead: new FormControl(false),
    maxPriceEnergyDayAhead: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    nsrMarketDayAhead: new FormControl(false),
    nsrsMarketDayAhead: new FormControl(false),
    priceEcrs: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    priceNsrs: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    priceRegDown: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    priceRegUp: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    priceRrs: new FormControl(0, Validators.compose([Validators.required, Validators.min(0)])),
    regDownMarketDayAhead: new FormControl(false),
    regUpMarketDayAhead: new FormControl(false),
    riskProfileDayAhead: new FormControl(0),
    // EMS-1017: these settings are not used. Conservatively keeping the code around in case they become useful
    // frupMarketDayAhead: new FormControl((this.site.config.frUpMarketDayAhead) ? this.site.config.frUpMarketDayAhead : false),
    // frdownMarketDayAhead: new FormControl((this.site.config.frDownmarketDayAhead) ? this.site.config.frDownmarketDayAhead : false),
    rrsMarketDayAhead: new FormControl(false),
    useHourlyASPricesDayAhead: new FormControl(false),
    useHourlyRiskProfileDayAhead: new FormControl(false),
  });

  defaultPricesToOfferDayAhead: PricesToOffer;
  pricesToOfferDayAhead: PricesToOffer;

  defaultPricesToOfferAS: PricesToOffer;
  pricesToOfferRegUp: PricesToOffer;
  pricesToOfferRegDown: PricesToOffer;
  pricesToOfferRrs: PricesToOffer;
  pricesToOfferEcrs: PricesToOffer;
  pricesToOfferNsrs: PricesToOffer;

  constructor(
    private alertService: GlobalAlertService,
    private marketsConfigService: MarketsConfigService,
    private programService: ProgramService,
    private bidService: BidService,
    private forecastService: ForecastService,
    private bidStrategyService: BidStrategyService,
    private permissionService: PermissionService,
    private siteService: SiteService,
    public dialog: MatDialog,
  ) {
    this.defaultPricesToOfferDayAhead = {
      extraConf: { aggressivity: '', method: '' },
      maxPrice: 105,
      minPrice: 0,
      nonSpin: null,
      strategy: 'taker',
    };
    this.defaultPricesToOfferAS = {
      extraConf: { aggressivity: '', method: '' },
      maxPrice: 200,
      minPrice: 10,
      nonSpin: null,
      strategy: 'taker',
    };
  }

  ngOnInit() {
    this.subject$ = new Subject<void>();
    this.initForm();
  }

  private initForm() {
    if (this.site && this.site.config) {
      this.pricesToOfferDayAhead = this.site.config.pricesToOfferEnergyDayAhead
        ? this.site.config.pricesToOfferEnergyDayAhead
        : this.defaultPricesToOfferDayAhead;
      this.pricesToOfferRegUp = this.site.config.pricesToOfferRegUp
        ? this.site.config.pricesToOfferRegUp
        : this.defaultPricesToOfferAS;
      this.pricesToOfferRegDown = this.site.config.pricesToOfferRegDown
        ? this.site.config.pricesToOfferRegDown
        : this.defaultPricesToOfferAS;
      this.pricesToOfferRrs = this.site.config.pricesToOfferRrs
        ? this.site.config.pricesToOfferRrs
        : this.defaultPricesToOfferAS;
      this.pricesToOfferEcrs = this.site.config.pricesToOfferEcrs
        ? this.site.config.pricesToOfferEcrs
        : this.defaultPricesToOfferAS;
      this.pricesToOfferNsrs = this.site.config.pricesToOfferNsrs
        ? this.site.config.pricesToOfferNsrs
        : this.defaultPricesToOfferAS;

      this.dayAheadForm = new FormGroup({
        dayAheadForecaster: new FormControl(this.site.config.dayAheadForecaster),
        ecrsMarketDayAhead: new FormControl(
          this.site.config.ecrsMarketDayAhead && this.site.config.ecrsMarketDayAhead === 'true',
        ),
        energyMarketDayAhead: new FormControl(
          this.site.config.energyMarketDayAhead && this.site.config.energyMarketDayAhead === 'true',
        ),
        ffrMarketDayAhead: new FormControl(
          this.site.config.ffrMarketDayAhead && this.site.config.ffrMarketDayAhead === 'true',
        ),
        loadMarketDayAhead: new FormControl(
          this.site.config.loadMarketDayAhead && this.site.config.loadMarketDayAhead === 'true',
        ),
        maxPriceEnergyDayAhead: new FormControl(
          this.pricesToOfferDayAhead.maxPrice,
          Validators.compose([Validators.required, Validators.min(0)]),
        ),
        nsrMarketDayAhead: new FormControl(
          this.site.config.nsrMarketDayAhead && this.site.config.nsrMarketDayAhead === 'true',
        ),
        nsrsMarketDayAhead: new FormControl(
          this.site.config.nsrsMarketDayAhead && this.site.config.nsrsMarketDayAhead === 'true',
        ),
        priceEcrs: new FormControl(
          this.pricesToOfferEcrs.minPrice,
          Validators.compose([Validators.required, Validators.min(0)]),
        ),
        priceNsrs: new FormControl(
          this.pricesToOfferNsrs.minPrice,
          Validators.compose([Validators.required, Validators.min(0)]),
        ),
        priceRegDown: new FormControl(
          this.pricesToOfferRegDown.minPrice,
          Validators.compose([Validators.required, Validators.min(0)]),
        ),
        priceRegUp: new FormControl(
          this.pricesToOfferRegUp.minPrice,
          Validators.compose([Validators.required, Validators.min(0)]),
        ),
        priceRrs: new FormControl(
          this.pricesToOfferRrs.minPrice,
          Validators.compose([Validators.required, Validators.min(0)]),
        ),
        regDownMarketDayAhead: new FormControl(
          this.site.config.regDownMarketDayAhead && this.site.config.regDownMarketDayAhead === 'true',
        ),
        regUpMarketDayAhead: new FormControl(
          this.site.config.regUpMarketDayAhead && this.site.config.regUpMarketDayAhead === 'true',
        ),
        riskProfileDayAhead: new FormControl(
          this.site.config.riskProfileDayAhead ? this.site.config.riskProfileDayAhead : 0,
        ),
        // EMS-1017: these settings are not used. Conservatively keeping the code around in case they become useful
        // frupMarketDayAhead: new FormControl((this.site.config.frUpMarketDayAhead) ? this.site.config.frUpMarketDayAhead : false),
        // frdownMarketDayAhead: new FormControl((this.site.config.frDownmarketDayAhead) ? this.site.config.frDownmarketDayAhead : false),
        rrsMarketDayAhead: new FormControl(
          this.site.config.rrsMarketDayAhead && this.site.config.rrsMarketDayAhead === 'true',
        ),
        useHourlyASPricesDayAhead: new FormControl(
          this.site.config.useHourlyAsPricesDayAhead && this.site.config.useHourlyAsPricesDayAhead === 'true',
        ),
        useHourlyRiskProfileDayAhead: new FormControl(
          this.site.config.useHourlyRiskProfileDayAhead && this.site.config.useHourlyRiskProfileDayAhead === 'true',
        ),
      });
      //this.riskProfileDa = (this.site.config.riskProfileDayAhead) ? this.site.config.riskProfileDayAhead : 0;

      this.showHourlyRiskProfileButton =
        this.site.config.useHourlyRiskProfileDayAhead && this.site.config.useHourlyRiskProfileDayAhead === 'true';
      this.showHourlyASPricesButton =
        this.site.config.useHourlyAsPricesDayAhead && this.site.config.useHourlyAsPricesDayAhead === 'true';
    }
  }

  updateUseHourlyRiskProfileDa(dayAheadMarket: DayAheadMarketConfig) {
    this.showHourlyRiskProfileButton = dayAheadMarket.useHourlyRiskProfileDayAhead;
  }

  updateUseHourlyASPricesDa(dayAheadMarket: DayAheadMarketConfig) {
    this.showHourlyASPricesButton = dayAheadMarket.useHourlyASPricesDayAhead;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initForm();
  }

  ngOnDestroy() {
    this.subject$.next();
    this.subject$.complete();
  }

  save() {
    this.pricesToOfferDayAhead.maxPrice = this.dayAheadForm.get('maxPriceEnergyDayAhead').value;
    this.pricesToOfferRegUp.minPrice = this.dayAheadForm.get('priceRegUp').value;
    this.pricesToOfferRegDown.minPrice = this.dayAheadForm.get('priceRegDown').value;
    this.pricesToOfferRrs.minPrice = this.dayAheadForm.get('priceRrs').value;
    this.pricesToOfferEcrs.minPrice = this.dayAheadForm.get('priceEcrs').value;
    this.pricesToOfferNsrs.minPrice = this.dayAheadForm.get('priceNsrs').value;

    const configuration: DayAheadMarketConfig = {
      dayAheadForecaster: this.dayAheadForm.get('dayAheadForecaster').value,
      ecrsMarketDayAhead: this.dayAheadForm.get('ecrsMarketDayAhead').value === true,
      energyMarketDayAhead: this.dayAheadForm.get('energyMarketDayAhead').value === true,
      ffrMarketDayAhead: this.dayAheadForm.get('ffrMarketDayAhead').value === true,
      frDownMarketDayAhead: this.site.config.frDownMarketDayAhead === true,
      frUpMarketDayAhead: this.site.config.frUpMarketDayAhead === true,
      hourlyDaForecastOverride: this.site.config.hourlyDaForecastOverride || null,
      hourlyEcrsCapsDayAhead: this.site.config.hourlyEcrsCapsDayAhead ? this.site.config.hourlyEcrsCapsDayAhead : null,
      hourlyEcrsPricesDayAhead: this.site.config.hourlyEcrsPricesDayAhead
        ? this.site.config.hourlyEcrsPricesDayAhead
        : null,
      hourlyNextDayRtForecastOverride: this.site.config.hourlyNextDayRtForecastOverride || null,
      hourlyNsrsCapsDayAhead: this.site.config.hourlyNsrsCapsDayAhead ? this.site.config.hourlyNsrsCapsDayAhead : null,
      hourlyNsrsPricesDayAhead: this.site.config.hourlyNsrsPricesDayAhead
        ? this.site.config.hourlyNsrsPricesDayAhead
        : null,
      hourlyRegDownCapsDayAhead: this.site.config.hourlyRegDownCapsDayAhead
        ? this.site.config.hourlyRegDownCapsDayAhead
        : null,
      hourlyRegDownPricesDayAhead: this.site.config.hourlyRegDownPricesDayAhead
        ? this.site.config.hourlyRegDownPricesDayAhead
        : null,
      hourlyRegUpCapsDayAhead: this.site.config.hourlyRegUpCapsDayAhead
        ? this.site.config.hourlyRegUpCapsDayAhead
        : null,
      hourlyRegUpPricesDayAhead: this.site.config.hourlyRegUpPricesDayAhead
        ? this.site.config.hourlyRegUpPricesDayAhead
        : null,
      hourlyRrsCapsDayAhead: this.site.config.hourlyRrsCapsDayAhead ? this.site.config.hourlyRrsCapsDayAhead : null,
      hourlyRrsPricesDayAhead: this.site.config.hourlyRrsPricesDayAhead
        ? this.site.config.hourlyRrsPricesDayAhead
        : null,
      loadMarketDayAhead: this.dayAheadForm.get('loadMarketDayAhead').value === true,
      nsrMarketDayAhead: this.dayAheadForm.get('nsrMarketDayAhead').value === true,

      nsrsMarketDayAhead: this.dayAheadForm.get('nsrsMarketDayAhead').value === true,
      pricesToOfferECRS: this.pricesToOfferEcrs,
      pricesToOfferEnergyDayAhead: this.pricesToOfferDayAhead,
      pricesToOfferNsrs: this.pricesToOfferNsrs,
      pricesToOfferRegDown: this.pricesToOfferRegDown,
      pricesToOfferRegUp: this.pricesToOfferRegUp,
      pricesToOfferRRS: this.pricesToOfferRrs,
      regDownMarketDayAhead: this.dayAheadForm.get('regDownMarketDayAhead').value === true,
      regUpMarketDayAhead: this.dayAheadForm.get('regUpMarketDayAhead').value === true,
      riskProfileDayAhead: this.dayAheadForm.get('riskProfileDayAhead').value,

      rrsMarketDayAhead: this.dayAheadForm.get('rrsMarketDayAhead').value === true,
      useHourlyASPricesDayAhead: this.dayAheadForm.get('useHourlyASPricesDayAhead').value === true,

      useHourlyRiskProfileDayAhead: this.dayAheadForm.get('useHourlyRiskProfileDayAhead').value === true,
    };

    this.marketsConfigService.putDayAheadConfig(this.site, configuration).subscribe(
      data => {
        this.alertService.setSuccess('Day ahead configuration has been saved successfully!');
        this.dayAheadForm.markAsPristine();
      },
      error => {
        console.error(error);
        this.alertService.setError('An error was detected while saving day ahead market configuration.');
      },
    );
  }

  cancel() {
    this.initForm();
    this.dayAheadForm.reset(this.dayAheadForm.value);
  }

  exportData(type: string) {
    const dialogRef = this.dialog.open(ExportModalComponent, { data: { exportType: type, site: this.site } });
    dialogRef.componentInstance.exportType = dayAhead;
    dialogRef.componentInstance.site = this.site;
    dialogRef.componentInstance.csvType = type;
    console.log('the dialog was opened');
  }

  createBidStrategy(isTest: boolean) {
    const testString = isTest ? ' TEST' : '';
    if (!window.confirm(`Are you sure you want to send this${testString} bid strategy?`)) {
      return;
    }

    if (this.site) {
      this.bidStrategyService
        .sendDayAheadMarketBidStrategy(this.site, isTest)
        .pipe(takeUntil(this.subject$))
        .subscribe(
          data => {
            this.alertService.setSuccess(`Day ahead${testString} bid strategy has been requested.`);
          },
          error => {
            console.error(error);
            this.alertService.setError(`An error was detected while creating${testString} bid strategy.`);
          },
        );
    }
  }

  openRiskProfilesModal(dayAheadMarket: DayAheadMarketConfig) {
    const modalReference = this.dialog.open(HourlyConfigModalComponent, { data: { backdrop: 'static', size: 'lg' } });

    modalReference.componentInstance.fieldsToUpdate = [
      {
        value: this.site.config.hourlyRegUpCapsDayAhead || [],
        columnName: 'REGUP Cap %',
        originalPropertyName: 'hourlyRegUpCapsDayAhead',
      } as HourlyConfigColumn<number[]>,
      {
        value: this.site.config.hourlyRegDownCapsDayAhead || [],
        columnName: 'REGDOWN Cap %',
        originalPropertyName: 'hourlyRegDownCapsDayAhead',
      } as HourlyConfigColumn<number[]>,
      {
        value: this.site.config.hourlyRrsCapsDayAhead || [],
        columnName: 'RRS Cap %',
        originalPropertyName: 'hourlyRrsCapsDayAhead',
      } as HourlyConfigColumn<number[]>,
      {
        value: this.site.config.hourlyEcrsCapsDayAhead || [],
        columnName: 'ECRS Cap %',
        originalPropertyName: 'hourlyEcrsCapsDayAhead',
      } as HourlyConfigColumn<number[]>,
      {
        value: this.site.config.hourlyNsrsCapsDayAhead || [],
        columnName: 'NSRS Cap %',
        originalPropertyName: 'hourlyNsrsCapsDayAhead',
      } as HourlyConfigColumn<number[]>,
    ];
    modalReference.componentInstance.headerText = 'Hourly Risk Profiles';
    modalReference.componentInstance.subHeaderText = '';
    modalReference.componentInstance.requireFields = true;

    modalReference.componentInstance.savedConfig.subscribe(savedConfig => {
      if (savedConfig != null) {
        dayAheadMarket.hourlyRegUpCapsDayAhead = savedConfig.hourlyRegUpCapsDayAhead;
        dayAheadMarket.hourlyRegDownCapsDayAhead = savedConfig.hourlyRegDownCapsDayAhead;
        dayAheadMarket.hourlyRrsCapsDayAhead = savedConfig.hourlyRrsCapsDayAhead;
        dayAheadMarket.hourlyEcrsCapsDayAhead = savedConfig.hourlyEcrsCapsDayAhead;
        dayAheadMarket.hourlyNsrsCapsDayAhead = savedConfig.hourlyNsrsCapsDayAhead;

        this.marketsConfigService.putDayAheadConfig(this.site, dayAheadMarket).subscribe(() => {
          // Update site settings in case we reopen the dialog
          this.site.config.hourlyRegUpCapsDayAhead = savedConfig.hourlyRegUpCapsDayAhead;
          this.site.config.hourlyRegDownCapsDayAhead = savedConfig.hourlyRegDownCapsDayAhead;
          this.site.config.hourlyRrsCapsDayAhead = savedConfig.hourlyRrsCapsDayAhead;
          this.site.config.hourlyEcrsCapsDayAhead = savedConfig.hourlyEcrsCapsDayAhead;
          this.site.config.hourlyNsrsCapsDayAhead = savedConfig.hourlyNsrsCapsDayAhead;
          // Clear the dirty flag
          this.dayAheadForm.markAsPristine();
          modalReference.close();
          this.alertService.setSuccess('Day ahead configuration has been saved successfully!');
        }),
          error => {
            console.error(error);
            this.alertService.setError('An error was detected while saving site configuration.');
            modalReference.componentInstance.showError = true;
          };
      }
    });
  }

  openASPricesModal(dayAheadMarket: DayAheadMarketConfig) {
    const modalReference = this.dialog.open(HourlyConfigModalComponent, { data: { backdrop: 'static', size: 'lg' } });

    modalReference.componentInstance.fieldsToUpdate = [
      {
        value: this.site.config.hourlyRegUpPricesDayAhead || [],
        columnName: 'REGUP Price',
        originalPropertyName: 'hourlyRegUpPricesDayAhead',
      } as HourlyConfigColumn<number[]>,
      {
        value: this.site.config.hourlyRegDownPricesDayAhead || [],
        columnName: 'REGDOWN Price',
        originalPropertyName: 'hourlyRegDownPricesDayAhead',
      } as HourlyConfigColumn<number[]>,
      {
        value: this.site.config.hourlyRrsPricesDayAhead || [],
        columnName: 'RRS Price',
        originalPropertyName: 'hourlyRrsPricesDayAhead',
      } as HourlyConfigColumn<number[]>,
      {
        value: this.site.config.hourlyEcrsPricesDayAhead || [],
        columnName: 'ECRS Price',
        originalPropertyName: 'hourlyEcrsPricesDayAhead',
      } as HourlyConfigColumn<number[]>,
      {
        value: this.site.config.hourlyNsrsPricesDayAhead || [],
        columnName: 'NSRS Price',
        originalPropertyName: 'hourlyNsrsPricesDayAhead',
      } as HourlyConfigColumn<number[]>,
    ];

    modalReference.componentInstance.headerText = 'Day Ahead AS Prices';
    modalReference.componentInstance.subHeaderText = '';
    modalReference.componentInstance.requireFields = true;

    modalReference.componentInstance.savedConfig.subscribe(savedConfig => {
      if (savedConfig != null) {
        dayAheadMarket.hourlyRegUpPricesDayAhead = savedConfig.hourlyRegUpPricesDayAhead;
        dayAheadMarket.hourlyRegDownPricesDayAhead = savedConfig.hourlyRegDownPricesDayAhead;
        dayAheadMarket.hourlyRrsPricesDayAhead = savedConfig.hourlyRrsPricesDayAhead;
        dayAheadMarket.hourlyEcrsPricesDayAhead = savedConfig.hourlyEcrsPricesDayAhead;
        dayAheadMarket.hourlyNsrsPricesDayAhead = savedConfig.hourlyNsrsPricesDayAhead;

        this.marketsConfigService.putDayAheadConfig(this.site, dayAheadMarket).subscribe(() => {
          // Update site settings in case we reopen the dialog
          this.site.config.hourlyRegUpPricesDayAhead = savedConfig.hourlyRegUpPricesDayAhead;
          this.site.config.hourlyRegDownPricesDayAhead = savedConfig.hourlyRegDownPricesDayAhead;
          this.site.config.hourlyRrsPricesDayAhead = savedConfig.hourlyRrsPricesDayAhead;
          this.site.config.hourlyEcrsPricesDayAhead = savedConfig.hourlyEcrsPricesDayAhead;
          this.site.config.hourlyNsrsPricesDayAhead = savedConfig.hourlyNsrsPricesDayAhead;
          // Clear the dirty flag
          this.dayAheadForm.markAsPristine();
          modalReference.close();
          this.alertService.setSuccess('Day ahead configuration has been saved successfully!');
        }),
          error => {
            console.error(error);
            this.alertService.setError('An error was detected while saving site configuration.');
            modalReference.componentInstance.showError = true;
          };
      }
    });
  }

  hasAccess(): boolean {
    return this.permissionService.hasPermission([PermissionEnum.update]);
  }

  openSiteConfigOverrides(marketConfig: DayAheadMarketConfig) {
    this.siteService.getSite(this.site.id).subscribe(site => {
      const modalReference = this.dialog.open(HourlyConfigModalComponent, {
        data: { backdrop: 'static', size: 'lg' },
      });

      modalReference.componentInstance.fieldsToUpdate = [
        {
          value: site.config.hourlyDaForecastOverride || [],
          columnName: 'Day Ahead LMP Forecast Override',
          originalPropertyName: 'hourlyDaForecastOverride'
        } as HourlyConfigColumn<number[]>,
        {
          value: site.config.hourlyNextDayRtForecastOverride || [],
          columnName: 'Next-Day Real Time SPP Forecast Override',
          originalPropertyName: 'hourlyNextDayRtForecastOverride'
        } as HourlyConfigColumn<number[]>,
      ];

      modalReference.componentInstance.headerText = 'Day Ahead Site Forecast Overrides';
      modalReference.componentInstance.subHeaderText = '*Resets after market closure';
      modalReference.componentInstance.requireFields = false;
      modalReference.componentInstance.savedConfig.subscribe(savedConfig => {
        if (savedConfig !== null) {
          marketConfig.hourlyDaForecastOverride = savedConfig.hourlyDaForecastOverride;
          marketConfig.hourlyNextDayRtForecastOverride = savedConfig.hourlyNextDayForecastOverride;

          this.marketsConfigService.putDayAheadConfig(this.site, marketConfig).subscribe(() => {
            this.site.config.hourlyDaForecastOverride = savedConfig.hourlyDaForecastOverride;
            this.site.config.hourlyNextDayRtForecastOverride = savedConfig.hourlyNextDatForecastOverride;
            this.dayAheadForm.markAsPristine();
            modalReference.close();
            this.alertService.setSuccess('Day ahead configuration has been saved successfully!');
          }),
            error => {
              console.error(error);
              this.alertService.setError('An error was detected while saving site configuration.');
              modalReference.componentInstance.showError = true;
            };
        }
      });
    });
  }

  openProgramConfigOverrides(programConfig: ProgramConfig) {
    const modalReference = this.dialog.open(HourlyConfigModalComponent, {
      data: { backdrop: 'static', size: 'lg' },
    });
    modalReference.componentInstance.fieldsToUpdate = [
      {
        value: programConfig.hourlyRegUpForecastOverride || [],
        columnName: 'REGUP Forecast Override',
        originalPropertyName: 'hourlyRegUpForecastOverride',
      } as HourlyConfigColumn<number[]>,
      {
        value: programConfig.hourlyRegDownForecastOverride || [],
        columnName: 'REGDOWN Forecast Override',
        originalPropertyName: 'hourlyRegDownForecastOverride',
      } as HourlyConfigColumn<number[]>,
      {
        value: programConfig.hourlyRrsForecastOverride || [],
        columnName: 'RRS Forecast Override',
        originalPropertyName: 'hourlyRrsForecastOverride',
      } as HourlyConfigColumn<number[]>,
      {
        value: programConfig.hourlyEcrsForecastOverride || [],
        columnName: 'ECRS Forecast Override',
        originalPropertyName: 'hourlyEcrsForecastOverride',
      } as HourlyConfigColumn<number[]>,
      {
        value: programConfig.hourlyNsrsForecastOverride || [],
        columnName: 'NSRS Forecast Override',
        originalPropertyName: 'hourlyNsrsForecastOverride',
      } as HourlyConfigColumn<number[]>,
    ];

    modalReference.componentInstance.headerText = 'Day Ahead AS Market Forecast Overrides';
    modalReference.componentInstance.subHeaderText = '*Changes will apply for ALL sites.  Resets after market closure.';
    modalReference.componentInstance.requireFields = false;
    modalReference.componentInstance.savedConfig.subscribe(savedConfig => {
      if (savedConfig !== null) {
        this.programService.putProgramConfig$(savedConfig).subscribe(config => {
          this.programConfig.hourlyRegUpForecastOverride = savedConfig.hourlyRegUpForecastOverride;
          this.programConfig.hourlyRegDownForecastOverride = savedConfig.hourlyRegDownForecastOverride;
          this.programConfig.hourlyRrsForecastOverride = savedConfig.hourlyRrsForecastOverride;
          this.programConfig.hourlyEcrsForecastOverride = savedConfig.hourlyEcrsForecastOverride;
          this.programConfig.hourlyNsrsForecastOverride = savedConfig.hourlyNsrsForecastOverride;
          this.dayAheadForm.markAsPristine();
          modalReference.close();
          this.alertService.setSuccess('Day ahead configuration has been saved successfully!');
        }),
          error => {
            console.error(error);
            this.alertService.setError('An error was detected while saving program configuration.');
            modalReference.componentInstance.showError = true;
          };
      }
    });
  }
}
